import type { WorkspaceDropPayload } from "@/pages/workspaces/components/WorkspaceDropZone";
import { cn } from "@/utils";
import {
  dataTransferItemsHaveDirectory,
  traverseDataTransferItems,
} from "@/utils/traverseDataTransferItems";
import React, { forwardRef, useCallback, useEffect, useRef } from "react";

/**
 * Every file in a drop payload, folders flattened. For flat destinations
 * (canvas / chat scope) directory structure has no meaning — the files
 * inside dropped folders upload as plain siblings.
 */
export function flattenDropPayloadFiles(payload: WorkspaceDropPayload): File[] {
  if (!payload.tree) return payload.files;
  return [
    ...payload.tree.looseFiles,
    ...payload.tree.files.map((entry) => entry.file),
  ];
}

interface RailOsFileDropZoneProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onDrop"> {
  onDrop: ((payload: WorkspaceDropPayload) => void) | null;
  children: React.ReactNode;
  className?: string;
  /** Matches `droppableId.railWorkspaceFolder` for deepest-target highlighting. */
  dropId?: string;
}

/**
 * OS file drop target for the files rail — no overlay. Ignores in-app
 * dnd-kit drags. Highlight is driven by `RailFilesDropHighlightProvider`
 * via `data-rail-drop-id` on this element.
 */
export const RailOsFileDropZone = forwardRef<
  HTMLDivElement,
  RailOsFileDropZoneProps
>(function RailOsFileDropZone(
  { onDrop, children, className, dropId, ...rest },
  ref,
) {
  const dragCounter = useRef(0);

  useEffect(
    () => () => {
      dragCounter.current = 0;
    },
    [],
  );

  const isFileDrag = (e: React.DragEvent): boolean =>
    e.dataTransfer.types.includes("Files");

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    const related = e.relatedTarget;
    if (
      related instanceof Node &&
      e.currentTarget.contains(related) &&
      related !== e.currentTarget
    ) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    const related = e.relatedTarget;
    if (related instanceof Node && e.currentTarget.contains(related)) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (!onDrop || !isFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;

      const items = e.dataTransfer.items
        ? Array.from(e.dataTransfer.items)
        : [];
      const files = Array.from(e.dataTransfer.files);
      const hasDirectory =
        items.length > 0 && dataTransferItemsHaveDirectory(items);

      if (!hasDirectory) {
        if (files.length > 0) onDrop({ files });
        return;
      }

      void (async () => {
        const tree = await traverseDataTransferItems(items);
        const totalCount =
          tree.files.length + tree.looseFiles.length + tree.folders.length;
        if (totalCount === 0) return;
        onDrop({ files: tree.looseFiles, tree });
      })();
    },
    [onDrop],
  );

  if (!onDrop) {
    return (
      <div
        ref={ref}
        data-rail-drop-id={dropId}
        className={cn("rounded-md transition-[outline-color]", className)}
        {...rest}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      data-rail-drop-id={dropId}
      className={cn("rounded-md transition-[outline-color]", className)}
      {...rest}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {children}
    </div>
  );
});
